import {
  Directive,
  AfterViewInit,
  OnDestroy
} from '@angular/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

/**
 * @directive AriaHiddenTooltipDirective
 * @selector [zbpAriaHiddenTooltip]
 *
 * @description
 * Marks the rendered `ngbTooltip` element as `aria-hidden="true"` when shown.
 * This hides the tooltip content from screen readers while keeping it visible
 * for sighted users—useful when the tooltip text duplicates an existing
 * `aria-label` or visible label.
 *
 * @remarks
 * **Warning:** Misuse of this directive can create accessibility violations.
 * If the tooltip provides *unique* information not available elsewhere, hiding it
 * from assistive technology will make that information inaccessible to screen reader users.
 * Always confirm that the tooltip content is redundant before applying this directive.
 *
 * @usage
 * ```html
 * <a aria-label="Useful information"
 *   ngbTooltip="Useful information"
 *   zbpAriaHiddenTooltip
 * >
 *   <zbp-icon></zbp-icon>
 * </a>
 * ```
 */
@Directive({
  selector: '[zbpAriaHiddenTooltip]',
  standalone: false
})
export class AriaHiddenTooltipDirective implements AfterViewInit, OnDestroy {
  private _shownTooltip: Subscription;

  constructor(private tooltip: NgbTooltip) {}

  ngAfterViewInit() {
    this._shownTooltip = this.tooltip.shown.subscribe(() => {
      const tooltipEl = document.querySelector('.tooltip.show');
      if (tooltipEl) {
        tooltipEl.setAttribute('aria-hidden', 'true');
      }
    });
  }

  ngOnDestroy() {
    if (this._shownTooltip) {
      this._shownTooltip.unsubscribe();
    }
  }
}
